import { takeLatest, select, call, put } from "redux-saga/effects";
import { logOut, requestFetchSubmissions, setSubmissions } from "@actions/userActions";
import {
  cancelSubmission,
  getSubmissions,
  updateOrDenySubmissionRequest,
} from "@utils/api-communication/RestApiCli";
import {
  RESET_STATE,
  USER_APPROVE_OR_DENY_SUBMISSION,
  USER_CANCEL_SUBMISSION,
  USER_REQUEST_SUBMISSIONS,
  USER_SET_LOG_OUT,
} from "../constants";
import { PayloadAction } from "@reduxjs/toolkit";
import { SubmissionUpdate } from "types/userTypes";
import { authMethods } from "@utils/AuthUtil";
import { SecureTokenManager } from "@utils/auth/secureTokenManager";
import { selectIsLoggedIn } from "@utils/redux-utils/selectors/userSelectors";
import { REHYDRATE } from "redux-persist";

function* handleFetchSubmissionsRequest() {
  try {
    const response = yield call(
      () =>
        new Promise((resolve, reject) => {
          getSubmissions(
            (data) => {
              resolve(data);
            },
            (error) => {
              reject(error);
            },
          );
        }),
    );

    yield put(setSubmissions(response.data));
  } catch (error) {
    console.error("Error in handleFetchSubmissionsRequest:", error);
  }
}

function* handleSubmissionApprovalRequest(action: PayloadAction<SubmissionUpdate>) {
  try {
    const submissionUpdate = action.payload;
    yield call(
      () =>
        new Promise((resolve, reject) => {
          updateOrDenySubmissionRequest(submissionUpdate, resolve, reject);
        }),
    );
    yield put(requestFetchSubmissions());
  } catch (error) {
    console.error("Error in handleSubmissionApprovalRequest:", error);
  }
}

function* handleCancelSubmissionRequest(action: PayloadAction<string>) {
  const submissionId = action.payload;
  const response = yield call(
    () =>
      new Promise((resolve, reject) => {
        cancelSubmission(submissionId, resolve, reject);
      }),
  );
  if (response) {
    yield put(requestFetchSubmissions());
  } else {
    console.error("Error in handleCancelSubmissionRequest: Failed to cancel submission");
  }
}

function* handleLogout(action: PayloadAction<string | undefined>) {
  const redirect = action.payload;
  // Clear all data from all the states and logout
  yield put({ type: RESET_STATE });
  authMethods.signOut(redirect);
}

function* handleRehydrate() {
  const loggedIn = yield select(selectIsLoggedIn);
  if (loggedIn && !SecureTokenManager.isAuthenticated()) {
    yield put(logOut("/login"));
  }
}

export default function* userSagas() {
  yield takeLatest(REHYDRATE, handleRehydrate);
  yield takeLatest(USER_REQUEST_SUBMISSIONS, handleFetchSubmissionsRequest);
  yield takeLatest(USER_APPROVE_OR_DENY_SUBMISSION, handleSubmissionApprovalRequest);
  yield takeLatest(USER_CANCEL_SUBMISSION, handleCancelSubmissionRequest);
  yield takeLatest(USER_SET_LOG_OUT, handleLogout);
}
